import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, timer, TimeoutError } from 'rxjs';
import { map, catchError, switchMap, tap, filter, take, timeout } from 'rxjs/operators';
import * as ExtractionRunActions from './extraction-run.actions';
import { ExtractionRunService } from '../../services/extraction-run.service';
import { DexieService } from '../../services/dexie.service';
import { Store } from '@ngrx/store';
import { TableNames } from '../../services/utils/dexie.utils';
import { ExtractionRun } from '../../services/model';
import { SnackbarService } from '../../services/snackbar/snackbar.service';

// DXT-045: async re-run polling configuration
const RERUN_POLL_INTERVAL_MS = 3000;
const RERUN_POLL_TIMEOUT_MS = 90000;

@Injectable()
export class ExtractionRunEffects {
  loadExtractionRuns$;
  loadExtractionRun$;
  fetchExtractionRunsByDocument$;
  rerunExtraction$;
  rerunExtractionNotify$;

  constructor(
    private readonly actions$: Actions,
    private readonly extractionRunService: ExtractionRunService,
    private readonly dexieService: DexieService,
    private readonly store: Store,
    private readonly snackbarService: SnackbarService
  ) {
    this.loadExtractionRuns$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRunActions.fetchExtractionRuns),
        switchMap(({ ids }) =>
          (ids
            ? this.dexieService.itemsByAttribute<ExtractionRun>(TableNames.EXTRACTION_RUN, 'id', ids[0])
            : this.dexieService.allItems<ExtractionRun>(TableNames.EXTRACTION_RUN)
          ).pipe(
            map((runs) => ({ runs, ids })),
            catchError(() => of({ runs: [], ids }))
          )
        ),
        tap(({ runs, ids }) => {
          if (runs.length > 0) {
            this.store.dispatch(ExtractionRunActions.loadExtractionRuns({ extractionRuns: runs }));
          }
        }),
        switchMap(({ ids }) => {
          if (ids) {
            return this.extractionRunService.getAll({ ids });
          }
          return this.extractionRunService.getAll();
        }),
        switchMap((runs) =>
          this.dexieService.updateTable(TableNames.EXTRACTION_RUN, runs).pipe(
            map(() => runs),
            catchError(() => of(runs))
          )
        ),
        map((runs) => ExtractionRunActions.loadExtractionRuns({ extractionRuns: runs })),
        catchError((error) => {
          console.error('Error loading extraction runs:', error);
          return of(ExtractionRunActions.loadExtractionRunFailed({ error: error.message || 'Failed to load extraction runs' }));
        })
      )
    );

    this.loadExtractionRun$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRunActions.fetchExtractionRun),
        switchMap(({ id }) =>
          this.dexieService.itemById<ExtractionRun>(TableNames.EXTRACTION_RUN, id).pipe(
            tap((run) => {
              if (run) {
                this.store.dispatch(ExtractionRunActions.loadExtractionRun({ extractionRun: run }));
              }
            }),
            switchMap(() => this.extractionRunService.getById(id)),
            tap((run) => this.dexieService.addOrUpdateItems<ExtractionRun>(TableNames.EXTRACTION_RUN, [run])),
            map((run) => ExtractionRunActions.loadExtractionRun({ extractionRun: run })),
            catchError((error) => of(ExtractionRunActions.loadExtractionRunFailed({ error })))
          )
        )
      )
    );

    // DXT-045: fetch all runs for a document by documentId (reflects newly created runs)
    this.fetchExtractionRunsByDocument$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRunActions.fetchExtractionRunsByDocument),
        switchMap(({ documentId }) =>
          this.extractionRunService.getAll({ documentId }).pipe(
            switchMap((runs) => this.cacheRuns(runs)),
            map((runs) => ExtractionRunActions.loadExtractionRuns({ extractionRuns: runs })),
            catchError((error) =>
              of(
                ExtractionRunActions.loadExtractionRunFailed({
                  error: error?.message || 'Failed to load extraction runs',
                })
              )
            )
          )
        )
      )
    );

    // DXT-045: trigger async re-run, then poll until new runs appear (or time out)
    this.rerunExtraction$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ExtractionRunActions.rerunExtraction),
        switchMap(({ documentId, baselineCount }) =>
          this.extractionRunService.triggerReextraction(documentId).pipe(
            switchMap(() => this.pollForNewRuns(documentId, baselineCount)),
            catchError((error) =>
              of(
                ExtractionRunActions.rerunExtractionFailure({
                  error: error?.error?.message || error?.message || 'Failed to re-run extraction',
                })
              )
            )
          )
        )
      )
    );

    // DXT-045: user feedback for the re-run lifecycle (no state dispatch)
    this.rerunExtractionNotify$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(
            ExtractionRunActions.rerunExtractionSuccess,
            ExtractionRunActions.rerunExtractionTimeout,
            ExtractionRunActions.rerunExtractionFailure
          ),
          tap((action) => {
            if (action.type === ExtractionRunActions.rerunExtractionSuccess.type) {
              this.snackbarService.showSuccess({ message: 'KI-Extraktion erfolgreich erneut ausgeführt.' });
            } else if (action.type === ExtractionRunActions.rerunExtractionTimeout.type) {
              this.snackbarService.showWarning({
                message: 'Die KI-Extraktion läuft noch. Bitte aktualisieren Sie die Ansicht in Kürze.',
              });
            } else {
              this.snackbarService.showError({ message: 'KI-Extraktion konnte nicht erneut ausgeführt werden.' });
            }
          })
        ),
      { dispatch: false }
    );
  }

  private cacheRuns(runs: ExtractionRun[]) {
    return this.dexieService.updateTable(TableNames.EXTRACTION_RUN, runs).pipe(
      map(() => runs),
      catchError(() => of(runs))
    );
  }

  private pollForNewRuns(documentId: string, baselineCount: number) {
    return timer(RERUN_POLL_INTERVAL_MS, RERUN_POLL_INTERVAL_MS).pipe(
      switchMap(() => this.extractionRunService.getAll({ documentId }).pipe(catchError(() => of([] as ExtractionRun[])))),
      filter((runs) => runs.length > baselineCount),
      take(1),
      timeout({ first: RERUN_POLL_TIMEOUT_MS }),
      switchMap((runs) => this.cacheRuns(runs)),
      map((runs) => ExtractionRunActions.rerunExtractionSuccess({ extractionRuns: runs })),
      catchError((error) =>
        of(
          error instanceof TimeoutError
            ? ExtractionRunActions.rerunExtractionTimeout({ documentId })
            : ExtractionRunActions.rerunExtractionFailure({ error: error?.message || 'Failed to re-run extraction' })
        )
      )
    );
  }
}
